import { last, merge } from 'lodash';

import { chatService } from 'src/config/rootService';
import { RootState } from 'src/config/Store';
import {
  Conversation,
  ConversationsPageInfo,
  ConversationsQueryVars,
  FetchStatus,
  Message,
  MessagesPageInfo,
  MessagesQueryResult,
  MessagesQueryVars,
} from 'src/models/Chat';

export interface ChatState {
  conversations: Record<Conversation['uuid'], Conversation>;
  currentConversation: Conversation['uuid'] | null;
  fetchStatus: FetchStatus;
  pageInfo: ConversationsPageInfo;
  messages: Record<Message['uuid'], Message>;
  messagesFetchStatus: FetchStatus;
  messagesPageInfo: MessagesPageInfo & { lastMessageUuid: string };
  search: string | null;
  unreadConversations: string[];
  memberTopicsOfFocus: string[];
}

export const INITIAL_STATE: ChatState = {
  conversations: {},
  currentConversation: null,
  fetchStatus: FetchStatus.NotStarted,
  pageInfo: {
    endCursor: '',
    hasNextPage: false,
  },
  messages: {},
  messagesFetchStatus: FetchStatus.NotStarted,
  messagesPageInfo: {
    lastMessageUuid: '',
    oldestMessageUuid: '',
    conversationUuid: '',
  },
  search: null,
  unreadConversations: [],
  memberTopicsOfFocus: [],
};

export const chat = {
  state: {
    ...INITIAL_STATE,
  },
  reducers: {
    setConversations: (state: ChatState, conversations: ChatState['conversations']): ChatState => {
      return {
        ...state,
        conversations: {
          ...state.conversations,
          ...conversations,
        },
        fetchStatus: FetchStatus.Completed,
      };
    },
    setCurrentConversation(
      state: ChatState,
      currentConversation: ChatState['currentConversation'],
    ): ChatState {
      return { ...state, currentConversation };
    },
    setMemberTopicsOfFocus(
      state: ChatState,
      memberTopicsOfFocus: ChatState['memberTopicsOfFocus'],
    ): ChatState {
      return { ...state, memberTopicsOfFocus };
    },
    setPageInfo: (state: ChatState, pageInfo: ChatState['pageInfo']): ChatState => {
      return { ...state, pageInfo };
    },
    setFetchStatus: (state: ChatState, fetchStatus: ChatState['fetchStatus']): ChatState => {
      return { ...state, fetchStatus };
    },
    clearMessages: (state: ChatState): ChatState => {
      return {
        ...state,
        messages: {},
        messagesFetchStatus: FetchStatus.NotStarted,
        messagesPageInfo: {
          oldestMessageUuid: '',
          conversationUuid: '',
          lastMessageUuid: '',
        },
      };
    },
    setMessages: (state: ChatState, messages: ChatState['messages']): ChatState => {
      return {
        ...state,
        messages: {
          ...state.messages,
          ...messages,
        },
        messagesFetchStatus: FetchStatus.Completed,
      };
    },
    setMessagesPageInfo: (
      state: ChatState,
      messagesPageInfo: ChatState['messagesPageInfo'],
    ): ChatState => {
      return { ...state, messagesPageInfo };
    },
    addMessage: (state: ChatState, message: Message): ChatState => {
      return {
        ...state,
        messages: {
          [message.uuid]: message,
          ...state.messages,
        },
      };
    },
    updateConversation: (
      state: ChatState,
      conversation: Partial<Conversation> & Pick<Conversation, 'uuid'>,
    ): ChatState => {
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [conversation.uuid]: merge(state.conversations[conversation.uuid], conversation),
        },
      };
    },
    updateMessage: (
      state: ChatState,
      message: Partial<Message> & Pick<Message, 'uuid'>,
    ): ChatState => {
      return {
        ...state,
        messages: {
          ...state.messages,
          [message.uuid]: {
            ...state.messages[message.uuid],
            ...message,
          },
        },
      };
    },
    addConversation: (state: ChatState, conversation: Conversation): ChatState => {
      return {
        ...state,
        conversations: {
          [conversation.uuid]: conversation,
          ...state.conversations,
        },
      };
    },
    setSearchValue: (state: ChatState, search: string): ChatState => {
      return {
        ...state,
        search,
      };
    },
    setUnread: (state: ChatState, unreadConversations: string[]): ChatState => {
      const existing = new Set(state.unreadConversations);

      unreadConversations.forEach((conversation) => {
        existing.add(conversation);
      });

      return {
        ...state,
        unreadConversations: Array.from(existing.values()),
      };
    },
    setRead(state: ChatState, readConversationUuid: string) {
      const unreadConversations = [...state.unreadConversations].filter(
        (conversationId) => conversationId !== readConversationUuid,
      );

      return {
        ...state,
        unreadConversations,
        hasUnread: unreadConversations.length >= 1,
      };
    },
    partialReset(state: ChatState): ChatState {
      return {
        ...INITIAL_STATE,
        unreadConversations: state.unreadConversations,
      };
    },
    reset(): ChatState {
      return INITIAL_STATE;
    },
  },
  effects: (dispatch) => ({
    async fetchConversations(variables: ConversationsQueryVars = {}): Promise<void> {
      const conversations = await chatService.fetchConversations(variables);

      const conversationsNodes = (conversations?.edges ?? []).reduce(
        (conversationsMap, conversation) => {
          return {
            ...conversationsMap,
            [conversation.node.uuid]: {
              ...conversation.node,
              lastActivityTimestamp: conversation.node.newestMessage
                ? conversation.node.newestMessage.sentAt
                : conversation.node.createdAt,
            },
          };
        },
        {},
      );

      if (conversations?.pageInfo) {
        dispatch.chat.setPageInfo(conversations?.pageInfo);
      }
      dispatch.chat.setConversations(conversationsNodes);
    },
    async startPollingUnreads(_: undefined, rootState: RootState): Promise<void> {
      const coachUuid = rootState.user.currentUser.uuid;
      const hasUnreadNow = await chatService.fetchAllUnreadConvos();

      dispatch.chat.setUnread(hasUnreadNow);

      chatService.checkUnreads(coachUuid, (hasUnread: string) => {
        dispatch.chat.setUnread([hasUnread]);
      });
    },
    async fetchFirstPageConversations(variables: ConversationsQueryVars = {}): Promise<void> {
      dispatch.chat.setFetchStatus(FetchStatus.Loading);
      dispatch.chat.fetchConversations(variables);
    },
    async fetchNextPage(page: number, rootState: RootState): Promise<void> {
      dispatch.chat.setFetchStatus(FetchStatus.LoadingMore);
      dispatch.chat.fetchConversations({
        after: rootState.chat.pageInfo.endCursor,
        memberName: rootState.chat.search,
      });
    },
    async searchConversations(name: string): Promise<void> {
      dispatch.chat.setFetchStatus(FetchStatus.Loading);
      dispatch.chat.partialReset();
      dispatch.chat.setSearchValue(name);
      await dispatch.chat.fetchFirstPageConversations({ memberName: name });
    },
    async addNewConversation(conversation: Conversation): Promise<void> {
      dispatch.chat.addConversation({
        ...conversation,
        lastActivityTimestamp: conversation.newestMessage
          ? conversation.newestMessage.sentAt
          : conversation.createdAt,
        isNewMatch: true,
        oldMatchNotContacted: false,
      });
    },
    async checkAndUpdateReadMessages(messages: Message[], rootState: RootState): Promise<void> {
      await Promise.all(
        messages.map(async (message) => {
          if (message.readAt && message.senderUuid !== rootState.user.currentUser.uuid) {
            return;
          }

          if (message.senderUuid === rootState.user.currentUser.uuid) {
            return;
          }

          return chatService.updateMessage(message);
        }),
      );
    },
    async fetchMessages(
      variables: MessagesQueryVars,
    ): Promise<MessagesQueryResult['getMessages'] | null> {
      const messages = await chatService.fetchMessages(variables);

      const messageRecords = (messages?.messages ?? []).reduce((records, message) => {
        return {
          ...records,
          [message.uuid]: message,
        };
      }, {});
      dispatch.chat.setMessages(messageRecords);

      if (messages?.pageInfo) {
        const lastMessage = last(messages?.messages);
        dispatch.chat.setMessagesPageInfo({
          ...messages.pageInfo,
          lastMessageUuid: lastMessage ? lastMessage.uuid : '',
        });
      }

      return messages;
    },
    async setActiveConversation(uuid: string | null, rootState: RootState): Promise<void> {
      dispatch.chat.clearMessages();
      if (uuid && uuid !== '') {
        if (!rootState.chat.conversations[uuid]) {
          await dispatch.chat.fetchConversations({ uuid });
        }
        const messages = await dispatch.chat.fetchMessages({ conversationUuid: uuid });
        dispatch.chat.checkAndUpdateReadMessages(messages?.messages ?? []);
        dispatch.chat.setRead(uuid);
      }

      dispatch.chat.setCurrentConversation(uuid);
    },
    fetchMoreMessages(_: unknown, rootState: RootState): void {
      dispatch.chat.fetchMessages({
        after: rootState.chat.messagesPageInfo.lastMessageUuid,
        conversationUuid: rootState.chat.messagesPageInfo.conversationUuid,
      });
    },
    async addNewMessage(newMessage: Message, rootState: RootState): Promise<void> {
      if (rootState.chat.currentConversation === newMessage.conversationUuid) {
        dispatch.chat.addMessage({
          ...newMessage,
          messageResources: newMessage.messageResources ?? [],
        });
      }
      if (rootState.chat.conversations[newMessage.conversationUuid]) {
        dispatch.chat.updateConversation({
          uuid: newMessage.conversationUuid,
          newestMessage: newMessage,
          myNewestMessage:
            newMessage.senderUuid === rootState.user.currentUser.uuid ? newMessage : undefined,
        });
      }
    },
    async updateMessageAndConversation(message: Message, rootState: RootState): Promise<void> {
      const oldMessage = rootState.chat.messages[message.uuid];

      if (oldMessage) {
        dispatch.chat.updateMessage({
          uuid: message.uuid,
          readAt: message.readAt ? message.readAt : oldMessage.readAt,
          content: message.content ? message.content : oldMessage.content,
        });
      }

      const conversation = rootState.chat.conversations[message.conversationUuid];

      if (
        conversation &&
        conversation.newestMessage &&
        conversation.newestMessage.uuid === message.uuid
      ) {
        dispatch.chat.updateConversation({
          uuid: message.conversationUuid,
          newestMessage: {
            readAt: message.readAt,
          },
        });
      }
    },
    async setMessageNotNewMatch(_: unknown, rootState: RootState): Promise<void> {
      const currentConversation = rootState.chat.currentConversation;
      dispatch.chat.updateConversation({
        uuid: currentConversation,
        isNewMatch: false,
        isMatchedOverFiveDaysAgo: false,
      });
    },
    async setActiveConversationTopicsOfFocus(memberUUID): Promise<void> {
      const topics = await chatService.fetchMemberTopicsOfFocus(memberUUID);

      dispatch.chat.setMemberTopicsOfFocus(topics);
    },
  }),
  selectors: (slice, createSelector) => ({
    unreadMessagesCount: () => {
      return createSelector(
        (state: RootState) =>
          Object.values(state.chat.conversations).map((conv) =>
            conv.active ? conv.newestMessage : null,
          ),
        (state) => state.user.currentUser,
        (state) => state.chat.currentConversation,
        (state) => state.router.location.pathname.includes('chat'),
        (newestMessages, currentUser, currentConversation, onChatRoute) => {
          return newestMessages.filter((message) => {
            return !newestMessageIsRead(message, currentUser, currentConversation, onChatRoute);
          }).length;
        },
      );
    },
  }),
};

export const newestMessageIsRead = (
  newestMessage,
  currentUser,
  currentConversation,
  onChatRoute,
): boolean => {
  // Mark the newestMessage as read if one of the following are true:
  //   a) if the newestMessage.readAt is a value, or
  //   b) if the newestMessage's sender is the currentUser, or
  //   c) if we are on the chat route AND this newestMessage's conversation is the currentConversation
  return (
    !newestMessage ||
    newestMessage.readAt ||
    newestMessage.senderUuid === currentUser.uuid ||
    (onChatRoute &&
      currentConversation &&
      newestMessage.conversationUuid === currentConversation.uuid)
  );
};
